import { injectIntl, IntlShape } from 'react-intl';
import { useMemo } from 'react';
import { get, isEmpty, uniqueId } from 'lodash';
import {
  getAdditionalGuarantees,
  getProvisionInfo,
  resolveRiskName,
} from 'containers/CourtManagerLegalProceedings/Provision/utils';
import { translate } from 'containers/services/translate';
import { RISK_NAME } from 'containers/CourtManagerLegalProceedings/Provision/constants';
import {
  RiskName,
  RiskNameWrapper,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/styled-components';
import messages from 'containers/CourtManagerLegalProceedings/Provision/Regulation/messages';
import { EMPTY_REGULATION_INFO } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/data-structure';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { VictimType } from 'typings/globalTypes';
import { ClaimRecapType } from 'typings/claimTypes';

export interface AssociatedRisksProps {
  intl: IntlShape;
  sagisClaim: any;
  claimRecap: ClaimRecapType;
  setRiskRecap: Function;
  riskRecap: any;
  riskData: any;
  setRiskData: Function;
  riskTypes: any;
  setRiskTypes: Function;
}

export const AssociatedRisks: React.FC<AssociatedRisksProps> = ({
  sagisClaim,
  claimRecap,
  setRiskData,
  riskData,
  riskTypes,
  setRiskTypes,
  ...props
}): JSX.Element => {
  const additionalGuarantees = useMemo(
    () => getAdditionalGuarantees(sagisClaim),
    [sagisClaim],
  );
  const initialRisk = {
    riskName: '',
    primaryMessage: '',
    data: [],
    regulationInfo: { ...EMPTY_REGULATION_INFO },
    regulationId: uniqueId(),
  };
  const onClickHandler = (el: any | { id: string }[]) => () => {
    if (el?.riskName === RISK_NAME.CORPOREAL) {
      const data = { ...riskData };
      const victimClaimRecap = get(claimRecap, 'victims', []).map(
        (victim: VictimType) => {
          return {
            ...victim,
            checked: false,
            regulationAmount: { expanded: true },
          };
        },
      );
      data['victims'] = victimClaimRecap;

      setRiskData(data);

      const risk = [...props.riskRecap];
      const currentRisk =
        risk.find((item: any) => item.riskName === RISK_NAME.CORPOREAL) || {};

      if (risk.indexOf(currentRisk) !== -1) {
        risk.splice(risk.indexOf(currentRisk), 1);
        data.victims = [];
        setRiskData(data);
        return props.setRiskRecap(risk);
      }
      risk.push({
        ...initialRisk,
        riskName: el?.riskName,
        riskID: get(el, 'riskId'),
        primaryMessage: translate(messages.riskRecapVictims, props),
      });
      return props.setRiskRecap(risk);
    }
    if (el?.riskName === RISK_NAME.MATERIEL) {
      const materialRisk = get(sagisClaim, 'offeredRisks', []).find(
        (item: any) => item.riskName === el?.riskName,
      );
      const vehicles = get(claimRecap, 'vehicules', [])
        .map((el: any) => {
          const foundOpposingParty = get(
            materialRisk,
            'opposingParties',
            [],
          ).some((party: any) => {
            return (
              el.firstName === party.firstName &&
              el.lastName === party.lastName &&
              el.registrationNumber === party.registrationNumber
            );
          });
          return foundOpposingParty ? el : {};
        })
        .filter((el: any) => !isEmpty(el));
      const data = { ...riskData };
      data.vehicles = [
        ...vehicles.map((vehicle: any) => {
          return {
            ...vehicle,
            checked: false,
            regulationAmount: { expanded: true },
          };
        }),
      ];
      setRiskData(data);
      const risk = [...props.riskRecap];
      const currentRisk =
        risk.find((type: any) => type.riskName === RISK_NAME.MATERIEL) || {};

      if (!isEmpty(currentRisk)) {
        risk.splice(risk.indexOf(currentRisk), 1);
        data.vehicles = [];
        setRiskData(data);
        return props.setRiskRecap(risk);
      }
      risk.push({
        ...initialRisk,
        riskName: el?.riskName,
        riskID: get(materialRisk, 'riskId'),
        primaryMessage: translate(messages.riskRecapVehicles, props),
      });

      return props.setRiskRecap(risk);
    } else {
      const data = { ...riskData };
      data.additionalGuarantees = [
        ...additionalGuarantees.map((el: any) => {
          return {
            id: el.riskName,
            riskID: el?.riskId,
            regulationAmount: { expanded: true },
          };
        }),
      ];
      setRiskData(data);
      const risk = [...props.riskRecap];
      const currentRisk =
        risk.find(
          (item: any) =>
            item.riskName !== RISK_NAME.MATERIEL &&
            item.riskName !== RISK_NAME.CORPOREAL,
        ) || {};

      if (!isEmpty(currentRisk)) {
        risk.splice(risk.indexOf(currentRisk), 1);
        data.additionalGuarantees = [];
        setRiskData(data);
        return props.setRiskRecap(risk);
      }
      risk.push({
        ...initialRisk,
        riskName: RISK_NAME.GARANTIES_ANNEXES,
        primaryMessage: translate(messages.riskRecapGarenties, props),
      });
      return props.setRiskRecap(risk);
    }
  };
  return (
    <main>
      <div>
        <span className="title">{translate(messages.riskTitle, props)}</span>
        <span className="secondaryTitle">
          {translate(messages.addRisk, props)}
        </span>
      </div>
      <RiskNameWrapper>
        {getProvisionInfo(sagisClaim)
          .filter(
            (el: any) =>
              el.riskName === RISK_NAME.CORPOREAL ||
              (el.riskName === RISK_NAME.MATERIEL &&
                featureService.hasFeature(FEATURES.REGULATION_MATERIAL)),
          )
          .map((el: any, index: number) => (
            <RiskName
              key={index}
              className={
                props.riskRecap.find(
                  (risk: any) => risk.riskName === el.riskName,
                ) && 'activeButton'
              }
              data-testid="risk"
              onClick={onClickHandler(el)}
            >
              <span>{resolveRiskName(el.riskName)}</span>
            </RiskName>
          ))}
        {!isEmpty(additionalGuarantees) &&
          featureService.hasFeature(FEATURES.REGULATION_MATERIAL) && (
            <RiskName
              id="additional-risk"
              onClick={onClickHandler(additionalGuarantees)}
              className={
                props.riskRecap.find(
                  (risk: any) => risk.riskName === RISK_NAME.GARANTIES_ANNEXES,
                ) && 'activeButton'
              }
            >
              <span>{RISK_NAME.GARANTIES_ANNEXES}</span>
            </RiskName>
          )}
      </RiskNameWrapper>
    </main>
  );
};

export default injectIntl(AssociatedRisks);
